import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import VideoCard from "@/components/base/VideoCard";
import ArticleCard from "@/components/base/ArticleCard";
import SEOSchema from "@/components/base/SEOSchema";
import { categories } from "@/mocks/categories";
import { videos } from "@/mocks/videos";
import { articles } from "@/mocks/articles";
import { faqItems } from "@/mocks/reputation";

function HomeSEO() {
  return (
    <>
      <SEOSchema
        type="Organization"
        data={{
          name: "17c com一起草在线",
          url: "https://17c.com",
          description: "免费在线视频播放平台，提供海量高清视频内容",
        }}
      />
      <SEOSchema
        type="WebSite"
        data={{
          name: "17c com一起草在线",
          url: "https://17c.com",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://17c.com/search?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }}
      />
      <SEOSchema
        type="WebPage"
        data={{
          name: "17c com一起草在线 - 免费在线视频播放平台",
          description:
            "17c com一起草在线是一个提供海量在线视频内容的免费播放平台，支持高清流畅播放，无需下载，随时随地畅享精彩视频资源。",
          url: "https://17c.com",
        }}
      />
      <SEOSchema
        type="FAQPage"
        data={{
          mainEntity: faqItems.map((item) => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: item.answer,
            },
          })),
        }}
      />
    </>
  );
}

function HeroSection() {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.scrollTo({ top: 800, behavior: "smooth" });
    }
  };

  return (
    <section className="relative w-full h-[520px] md:h-[620px] flex items-center justify-center overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=Abstract%20geometric%20pattern%20with%20warm%20coral%20and%20cream%20gradient%2C%20soft%20flowing%20organic%20shapes%2C%20modern%20minimalist%20design%2C%20dark%20subtle%20texture%2C%20cinematic%20atmosphere%2C%20elegant%20and%20clean%20aesthetic%2C%20high%20contrast%20with%20light%20areas&width=1600&height=700&seq=hero-bg-home&orientation=landscape"
        alt=""
        className="absolute inset-0 w-full h-full object-cover object-top"
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
      <div className="relative z-10 w-full max-w-[900px] mx-auto px-4 md:px-6 text-center">
        <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 font-heading leading-tight">
          {t("home:heroTitle")}
        </h1>
        <p className="text-base md:text-lg text-white/80 mb-8 max-w-[600px] mx-auto">
          {t("home:heroSubtitle")}
        </p>
        <form onSubmit={handleSearch} className="max-w-[560px] mx-auto relative">
          <label htmlFor="hero-search" className="sr-only">
            搜索视频
          </label>
          <input
            id="hero-search"
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="搜索你喜欢的电影、电视剧、综艺..."
            className="w-full h-12 md:h-14 pl-5 pr-32 rounded-full bg-white/95 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
          <button
            type="submit"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 h-9 md:h-11 px-6 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-search-line mr-1.5"></i>
            搜索
          </button>
        </form>
        <div className="flex items-center justify-center flex-wrap gap-3 mt-8">
          {categories.slice(0, 6).map((cat) => (
            <Link
              key={cat.id}
              to={`/category/${cat.slug}`}
              className="px-4 py-2 rounded-full bg-white/15 text-white text-sm hover:bg-white/25 transition-colors backdrop-blur-sm cursor-pointer whitespace-nowrap"
            >
              {cat.name}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function StatsBar() {
  const { t } = useTranslation();
  const stats = [
    { icon: "ri-vidicon-line", value: "50,000+", label: t("home:statsVideos") },
    { icon: "ri-user-line", value: "800万+", label: t("home:statsUsers") },
    { icon: "ri-stack-line", value: "8", label: t("home:statsCategories") },
    { icon: "ri-refresh-line", value: "200+", label: t("home:statsDaily") },
  ];

  return (
    <section className="bg-background-50">
      <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-8 md:py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {stats.map((stat, idx) => (
            <div
              key={idx}
              className="flex flex-col items-center text-center p-5 rounded-lg bg-background-100"
            >
              <div className="w-10 h-10 flex items-center justify-center bg-primary-100 text-primary-600 rounded-full mb-3">
                <i className={`${stat.icon} text-lg`}></i>
              </div>
              <span className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">
                {stat.value}
              </span>
              <span className="text-xs text-foreground-500 mt-1">{stat.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CategorySection() {
  const { t } = useTranslation();

  return (
    <section className="bg-background-100">
      <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-12 md:py-16">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
            {t("home:categoryTitle")}
          </h2>
          <Link
            to="/category/dianying"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap flex items-center gap-1"
          >
            {t("viewMore")}
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/category/${cat.slug}`}
              className="group flex items-center gap-4 p-4 md:p-5 rounded-lg bg-background-50 hover:bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 group-hover:bg-accent-500 group-hover:text-background-50 transition-colors shrink-0">
                <i className={`${cat.icon} text-xl`}></i>
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-foreground-900 font-heading">
                  {cat.name}
                </h3>
                <p className="text-xs text-foreground-500 mt-0.5">
                  {cat.videoCount.toLocaleString()}部视频
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function HotVideosSection() {
  const { t } = useTranslation();
  const hotVideos = videos.filter((v) => v.isHot).slice(0, 8);

  return (
    <section className="bg-background-50">
      <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-12 md:py-16">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
            {t("home:hotToday")}
          </h2>
          <Link
            to="/category/dianying"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap flex items-center gap-1"
          >
            {t("viewMore")}
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {hotVideos.map((video) => (
            <VideoCard key={video.id} {...video} />
          ))}
        </div>
      </div>
    </section>
  );
}

function LatestVideosSection() {
  const { t } = useTranslation();
  const newVideos = videos.filter((v) => v.isNew).slice(0, 8);

  return (
    <section className="bg-background-100">
      <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-12 md:py-16">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
            {t("home:latestUpdate")}
          </h2>
          <Link
            to="/category/dianying"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap flex items-center gap-1"
          >
            {t("viewMore")}
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {newVideos.map((video) => (
            <VideoCard key={video.id} {...video} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturedArticlesSection() {
  const { t } = useTranslation();

  return (
    <section className="bg-background-50">
      <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-12 md:py-16">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
            {t("home:featureTitle")}
          </h2>
          <Link
            to="/category/dianying"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap flex items-center gap-1"
          >
            {t("viewMore")}
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {articles.slice(0, 6).map((article) => (
            <ArticleCard key={article.id} {...article} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="bg-background-100">
      <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-12 md:py-16">
        <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading text-center mb-10">
          常见问题
        </h2>
        <div className="max-w-[800px] mx-auto space-y-3">
          {faqItems.map((item, index) => (
            <div
              key={index}
              className="rounded-lg bg-background-50 border border-background-200/70 overflow-hidden"
            >
              <button
                className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                aria-expanded={openIndex === index}
              >
                <h4 className="text-sm font-medium text-foreground-900 pr-4">
                  {item.question}
                </h4>
                <i
                  className={`ri-${openIndex === index ? "subtract" : "add"}-line text-foreground-400 text-lg shrink-0 transition-transform duration-200`}
                ></i>
              </button>
              {openIndex === index && (
                <div className="px-5 pb-4">
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {item.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function NewsletterSection() {
  const [email, setEmail] = useState("");
  const [statusMsg, setStatusMsg] = useState("");
  const [isError, setIsError] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();

    if (honeypot) {
      setStatusMsg("订阅成功！感谢您的关注。");
      setIsError(false);
      setEmail("");
      return;
    }

    if (!email.trim()) {
      setStatusMsg("请输入有效的邮箱地址");
      setIsError(true);
      return;
    }

    try {
      const formData = new FormData(form);
      const res = await fetch("https://readdy.ai/api/form/d9phgtdi2vhsdh2d34gg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (res.ok && parsed?.code === "OK") {
        setStatusMsg("订阅成功！感谢您的关注。");
        setIsError(false);
        setEmail("");
      } else if (serverMsg?.includes("spam") || serverMsg?.includes("form data is spam")) {
        setStatusMsg("订阅成功！感谢您的关注。");
        setIsError(false);
        setEmail("");
      } else {
        setStatusMsg(serverMsg || "订阅失败，请稍后重试");
        setIsError(true);
      }
    } catch {
      setStatusMsg("网络错误，请稍后重试");
      setIsError(true);
    }
  };

  return (
    <section className="bg-accent-500">
      <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-12 md:py-16">
        <div className="max-w-[600px] mx-auto text-center">
          <h2 className="text-xl md:text-2xl font-bold text-background-50 font-heading mb-3">
            订阅我们的最新资讯
          </h2>
          <p className="text-sm text-background-50/80 mb-6">
            第一时间获取新片上线、热门推荐和独家内容更新通知
          </p>
          <form
            onSubmit={handleSubmit}
            data-readdy-form
            className="flex flex-col sm:flex-row gap-3 max-w-[480px] mx-auto"
          >
            <input
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden={true}
              readOnly
              className="honeypot-field"
            />
            <label htmlFor="newsletter-email" className="sr-only">
              邮箱地址
            </label>
            <input
              id="newsletter-email"
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="请输入您的邮箱地址"
              className="flex-1 h-11 px-4 rounded-full bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-background-50/50"
              required
            />
            <button
              type="submit"
              className="h-11 px-6 rounded-full bg-foreground-950 text-background-50 text-sm font-medium hover:bg-foreground-900 transition-colors cursor-pointer whitespace-nowrap"
            >
              立即订阅
            </button>
          </form>
          {statusMsg && (
            <p
              className={`mt-3 text-sm ${isError ? "text-red-200" : "text-background-50/90"}`}
            >
              {statusMsg}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <HomeSEO />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <HeroSection />
          <StatsBar />
          <CategorySection />
          <HotVideosSection />
          <LatestVideosSection />
          <FeaturedArticlesSection />
          <FAQSection />
          <NewsletterSection />
        </main>
        <Footer />
      </div>
    </>
  );
}